import React, { useState , useEffect} from "react";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Box,
  Divider,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Fab,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
    Tooltip
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import { auth } from "../../../_services";
import SearchIcon from "@mui/icons-material/Search";
import InputAdornment from "@mui/material/InputAdornment";
import TextField from "@mui/material/TextField";
import Link from "@mui/material/Link";
import Stack from "@mui/material/Stack";
import PhoneIcon from "@mui/icons-material/Phone";
import EmailIcon from "@mui/icons-material/Email";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";

const ResponsiveAvailabilityView = ({
  data,
  shiftData,
  dayData,
  userData = [],
  t,
  jwt,
  showUser = false,
  highlightUserId,
  onAdd,
  onDelete, allowManageOwn, canManage
}) => {
  const userId = auth.getUserId(jwt);

  if (showUser && (!userData || userData.length === 0)) return null;

  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedDayId, setSelectedDayId] = useState("");
  const [selectedShiftId, setSelectedShiftId] = useState("");
  const [itemToDelete, setItemToDelete] = useState(null);
  const [searchText, setSearchText] = useState("");
  const [expandedDays, setExpandedDays] = useState({});
  const [expandedShifts, setExpandedShifts] = useState({});
  
  const orderedDays = [
    "Lunedì",
    "Martedì",
    "Mercoledì",
    "Giovedì",
    "Venerdì",
    "Sabato",
    "Domenica"
  ];

  const grouped = {};

  data.forEach((item) => {
    const day = dayData.find((d) => d.id === item.anaDayId)?.name;
    const shift = shiftData.find((s) => s.id === item.anaShiftId)?.name;
    if (!day || !shift) return;

    if (!grouped[day]) grouped[day] = {};
    if (!grouped[day][shift]) grouped[day][shift] = [];
    grouped[day][shift].push(item);
  });

  const filteredDays = Object.entries(grouped)
    .filter(([dayName, shifts]) => showUser || Object.values(shifts).flat().some(i => i.anaUserId === userId))
    .sort((a, b) => orderedDays.indexOf(a[0]) - orderedDays.indexOf(b[0]))
    .filter(([dayName, shifts]) => {
      if (!searchText.trim()) return true;
      const lower = searchText.toLowerCase();
      if (dayName.toLowerCase().includes(lower)) return true;
      return Object.entries(shifts).some(([shiftName, records]) =>
        shiftName.toLowerCase().includes(lower) ||
        records.some(item =>
          (userData.find(u => u.id === item.anaUserId)?.name || "")
            .toLowerCase().includes(lower)
        )
      );
    });

  const handleAdd = () => {
    if (selectedDayId && selectedShiftId && onAdd) {
      onAdd({
        anaDayId: parseInt(selectedDayId),
        anaShiftId: parseInt(selectedShiftId),
        anaUserId: userId,
        id: -1,
        createdAt: new Date()
      });
      setAddDialogOpen(false);
      setSelectedDayId("");
      setSelectedShiftId("");
    }
  };
  useEffect(() => {
    if (searchText.trim() === "") {
      setExpandedDays({});
      setExpandedShifts({});
      return;
    }
    const newDays = {};
    const newShifts = {};
    filteredDays.forEach(([dayName, shifts]) => {
      newDays[dayName] = true;
      Object.keys(shifts).forEach(shiftName => {
        newShifts[`${dayName}_${shiftName}`] = true;
      });
    });
    setExpandedDays(newDays);
    setExpandedShifts(newShifts);
  }, [searchText]);

  const handleConfirmDelete = () => {
    if (itemToDelete && onDelete) {
      onDelete(itemToDelete);
      setItemToDelete(null);
      setDeleteDialogOpen(false);
    }
  };
  const parseContact = (mobile) => {
    if (!mobile) return { phone: null, email: null };
    const parts = mobile.split(" ; ");
    const phone = parts[0] === "-" ? null : parts[0].trim().replace(/\s+/g, "");
    const email = parts[1]?.trim() || null;
    return { phone, email };
  };

  return (
    <Box>
    {showUser && (<TextField
        fullWidth
        size="small"
        placeholder={t("Cerca volontario...")}
        value={searchText}
        onChange={(e) => setSearchText(e.target.value)}
        sx={{ mb: 2 }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon fontSize="small" />
            </InputAdornment>
          )
        }}
      />
      )}
      {canManage && (!showUser || allowManageOwn) && (
        <Tooltip title={t("addAvailability")} placement="top">
            <Fab
              color="primary"
              sx={{ position: "fixed", bottom: 80, right: 18, zIndex: 9999 }}
              onClick={() => setAddDialogOpen(true)}
              >
              <AddIcon />
            </Fab>
        </Tooltip>
      )}
      {filteredDays.length === 0 && (
        <Typography color="text.secondary" sx={{ mt: 2, textAlign: "center" }}>
          {t("Nessun risultato trovato")}
        </Typography>
      )}
      {filteredDays.map(([dayName, dayEntries]) => {
        const hasMine = Object.values(dayEntries).flat().some(
          (i) => (highlightUserId ?? userId) === i.anaUserId
        );
        return (
          <Accordion
            key={dayName}
            expanded={expandedDays[dayName] ?? false}
            onChange={(_, isExpanded) =>
              setExpandedDays(prev => ({ ...prev, [dayName]: isExpanded }))
            }
            sx={{ mb: 2, borderLeft: hasMine ? "4px solid #1976d2" : "none" }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography fontWeight="bold">{dayName}</Typography>
            </AccordionSummary>
            <AccordionDetails>
              {Object.entries(dayEntries).map(([shiftName, records]) => {
                const sortedRecords = [...records].sort((a, b) => {
                  const nameA = userData.find(u => u.id === a.anaUserId)?.name || "";
                  const nameB = userData.find(u => u.id === b.anaUserId)?.name || "";
                  return nameA.localeCompare(nameB);
                });
                const lower = searchText.toLowerCase().trim();
                const visibleRecords = lower
                  ? sortedRecords.filter(item => {
                      const userName = userData.find(u => u.id === item.anaUserId)?.name || "";
                      return userName.toLowerCase().includes(lower);
                    })
                  : sortedRecords;
                if (searchText.trim() && visibleRecords.length === 0) return null;
                const hasMineInShift = visibleRecords.some(
                  (i) => (highlightUserId ?? userId) === i.anaUserId
                );
                return (
                  <Accordion
                    key={`${dayName}_${shiftName}`}
                    expanded={expandedDays[dayName] ?? false}
                    onChange={(_, isExpanded) =>
                      setExpandedDays(prev => ({ ...prev, [dayName]: isExpanded }))
                    }
                    sx={{
                      mb: 1,
                      borderRadius: "4px",
                      borderLeft: hasMineInShift ? "4px solid #1976d2" : "none"
                    }}
                  >
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Box display="flex" alignItems="center">
                        <Typography>{shiftName}</Typography>
                        <Box
                          sx={{
                            backgroundColor: "primary.main",
                            color: "white",
                            fontWeight: "bold",
                            fontSize: "0.75rem",
                            borderRadius: "50%",
                            width: 20,
                            height: 20,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            ml: 1
                          }}
                        >
                          {searchText.trim() ? visibleRecords.length : sortedRecords.length}
                        </Box>
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails>
                      {visibleRecords.map((item, idx) => {
                        const userName = userData.find(u => u.id === item.anaUserId)?.name || "-";
                        const isMine = (highlightUserId ?? userId) === item.anaUserId;
                        return (
                          <Paper
                            key={idx}
                            sx={{
                              p: 2,
                              mb: 1,
                              backgroundColor: isMine ? "#ABEBC6" : "inherit",
                              position: "relative"
                            }}
                            elevation={1}
                          >
                            {canManage &&(!showUser || allowManageOwn) && isMine && (
                              <IconButton
                                size="small"
                                onClick={() => {
                                  setItemToDelete(item);
                                  setDeleteDialogOpen(true);
                                }}
                                sx={{ position: "absolute", top: 4, right: 4 }}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            )}

                            {showUser && (
                              <>
                                <Typography variant="body2">
                                  <strong>{t("anaUserId")}:</strong> {userName}
                                </Typography>
                                <Divider sx={{ my: 1 }} />
                              </>
                            )}
                            <Typography variant="body2">
                              <strong>{t("userRoleList")}:</strong> {item.userRoleList || "-"}
                            </Typography>
                            {item.mobile && (() => {
                              const { phone, email } = parseContact(item.mobile);
                              return (
                                <Stack direction="row" spacing={1} sx={{ mt: 1 }} alignItems="center">
                                  {email && (
                                    <Tooltip title={email} placement="top">
                                      <Link href={`mailto:${email}`} target="_blank" rel="noreferrer">
                                        <IconButton size="small" color="primary">
                                          <EmailIcon fontSize="small" />
                                        </IconButton>
                                      </Link>
                                    </Tooltip>
                                  )}
                                  {phone && (
                                    <Tooltip title={phone} placement="top">
                                      <Link href={`tel:+39${phone}`}>
                                        <IconButton size="small" color="primary">
                                          <PhoneIcon fontSize="small" />
                                        </IconButton>
                                      </Link>
                                    </Tooltip>
                                  )}
                                  {phone && (
                                    <Tooltip title={`WhatsApp: +39${phone}`} placement="top">
                                      <Link
                                        href={`https://wa.me/39${phone}`}
                                        target="_blank"
                                        rel="noreferrer"
                                      >
                                        <IconButton size="small" sx={{ color: "#25D366" }}>
                                          <WhatsAppIcon fontSize="small" />
                                        </IconButton>
                                      </Link>
                                    </Tooltip>
                                  )}
                                </Stack>
                              );
                            })()}
                          </Paper>
                        );
                      })}
                    </AccordionDetails>
                  </Accordion>
                );
              })}
            </AccordionDetails>
          </Accordion>
        );
      })}

      <Dialog open={addDialogOpen} onClose={() => setAddDialogOpen(false)}
        fullWidth
        maxWidth="xs"
        PaperProps={{
          sx: {
            width: 400,
            maxWidth: '90%',
          }
        }}>
        <DialogTitle>{t("addAvailability")}</DialogTitle>
        <DialogContent>
          <FormControl fullWidth margin="normal">
            <InputLabel>{t("anaDayId")}</InputLabel>
            <Select
              value={selectedDayId}
              label={t("anaDayId")}
              onChange={(e) => setSelectedDayId(e.target.value)}
            >
              {dayData.map((d) => (
                <MenuItem key={d.id} value={d.id}>
                  {d.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth margin="normal">
            <InputLabel>{t("anaShiftId")}</InputLabel>
            <Select
              value={selectedShiftId}
              label={t("anaShiftId")}
              onChange={(e) => setSelectedShiftId(e.target.value)}
            >
              {shiftData.map((s) => (
                <MenuItem key={s.id} value={s.id}>
                  {s.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddDialogOpen(false)}>{t("cancel")}</Button>
          <Button variant="contained" onClick={handleAdd}>
            {t("confirm")}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}
        fullWidth
        maxWidth="xs"
        PaperProps={{
          sx: {
            width: 400,
            maxWidth: '90%',
          }
        }}>
        <DialogTitle>{t("confirmDelete")}</DialogTitle>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>{t("cancel")}</Button>
          <Button color="primary" variant="contained" onClick={handleConfirmDelete}>
            {t("delete")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ResponsiveAvailabilityView;
